.shell {
  display: flex;
  min-height: 100vh;
  background: var(--bg);
}

/* ------------------------------------------------------------- sidebar */
.sidebar {
  width: var(--sidebar-width);
  flex-shrink: 0;
  background: var(--sidebar-bg);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
}

.brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-5) var(--space-4);
  border-bottom: 1px solid rgb(255 255 255 / 8%);
}

.brandMark {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  /* The platform's own gradient, matching the public site and the sign-in
     screen. The mark inside is white, so it reads on the dark sidebar. */
  background: linear-gradient(135deg, #36699c, #1b4d3e);
  border-radius: 10px;
  flex-shrink: 0;
}

.brandMark svg {
  width: 20px;
  height: 20px;
}

/* The client's own logo, in place of the platform mark.
   *** WHITE, NOT THE PLATFORM GRADIENT ***
   The gradient above exists so a WHITE svg reads on the dark sidebar. A
   client's logo is their own colours — most are drawn for white paper, and
   Sure60's red and blue on a dark green gradient is unreadable. A white tile
   is what their brand was designed against.
   `contain` because a logo is rarely square; `cover` would crop it. */
.brandLogo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff;
  border-radius: 10px;
  padding: 3px;
  /* Covers the parent's gradient: a logo with a transparent background would
     otherwise sit on dark green. */
  position: relative;
  z-index: 1;
}

/* Stacks the product name over the client's name. */
.brandText {
  display: flex;
  flex-direction: column;
  /* Without this, a long client name forces the sidebar wider instead of
     being truncated by the rule below. */
  min-width: 0;
  line-height: 1.25;
}

.brandName {
  color: #fff;
  font-weight: var(--weight-semibold);
  font-size: var(--text-md);
  white-space: nowrap;
}

/* *** THE CLIENT, NOT THE PRODUCT ***
   Deliberately quieter than the product name: it answers "whose account is
   this?" without competing with the brand above it. */
.brandTenant {
  color: rgb(255 255 255 / 58%);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  /* Client names are arbitrary length. Truncating keeps the sidebar's width
     fixed; the full name is in the title attribute. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav {
  padding: var(--space-4) var(--space-3);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  /* Takes the slack so the footer below sits at the bottom of the sidebar on
     a short menu, and is pushed below the fold on a long one — never floating
     mid-column. */
  flex: 1;
}

/* *** THE COMPANY THAT BUILDS THE PLATFORM ***
   Not the client. Sits under a hairline at the foot of the sidebar, matching
   the reference the user supplied. */
.sidebarFooter {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  margin-top: var(--space-4);
  border-top: 1px solid rgb(255 255 255 / 8%);
  color: rgb(255 255 255 / 38%);
  font-size: var(--text-xs);
  /* Left, matching the nav above it — centred text under a left-aligned
     nav reads as a different kind of thing. */
  text-align: left;
}

/*
  Privacy / Terms / Refund / Contact, folded behind one line.

  *** NOT nowrap ***
  The footer used to hold one line and forbade wrapping. The links need to
  wrap, and "Terms of Service" alone would overflow a 248px sidebar.
*/
.policyBox {
  text-align: left;
}

.policySummary {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) 0;
  color: rgb(255 255 255 / 55%);
  cursor: pointer;
  /* The default triangle is drawn by the UA and ignores the colour above. */
  list-style: none;
}

.policySummary::-webkit-details-marker {
  display: none;
}

/* A chevron that turns when the section opens. */
.policySummary::before {
  content: '';
  width: 0.4rem;
  height: 0.4rem;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(-45deg);
  transition: transform var(--transition-fast);
}

.policyBox[open] .policySummary::before {
  transform: rotate(45deg);
}

.policySummary:hover {
  color: #fff;
}

.policyNav {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-2) 0 0 var(--space-4);
}

.policyLink {
  color: rgb(255 255 255 / 55%);
  text-decoration: none;
  /* A legal link has to stay tappable without stretching the sidebar. */
  padding: 2px 0;
}

.policyLink:hover {
  color: #fff;
  text-decoration: underline;
  text-underline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .policySummary::before {
    transition: none;
  }
}

.copyright {
  white-space: nowrap;
}

.navGroup {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.navGroupTitle {
  padding: 0 var(--space-3) var(--space-2);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgb(255 255 255 / 35%);
}

.navLink {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius);
  color: var(--sidebar-text);
  font-size: var(--text-base);
  font-weight: var(--weight-medium);
  text-decoration: none;
  transition: background var(--transition-fast), color var(--transition-fast);
}

.navLink:hover {
  background: rgb(255 255 255 / 6%);
  color: #fff;
  text-decoration: none;
}

.navLinkActive {
  background: var(--sidebar-active);
  color: #fff;
}
.navLinkActive:hover { background: var(--sidebar-active); }

/* Sized for an inline SVG rather than a text glyph: the icons inherit
   currentColor, so the active and hover states below need no extra rules. */
.navIcon {
  width: 18px;
  height: 18px;
  display: grid;
  place-items: center;
  opacity: 0.85;
  flex-shrink: 0;
}

.navLink:hover .navIcon,
.navLinkActive .navIcon {
  opacity: 1;
}

/* ---------------------------------------------------------------- main */
.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;   /* lets wide tables scroll instead of stretching the layout */
}

.topbar {
  height: var(--topbar-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: 0 var(--space-5);
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  z-index: var(--z-sticky);
}

.menuButton {
  display: none;
  background: transparent;
  border: none;
  font-size: var(--text-lg);
  color: var(--text);
  padding: var(--space-2);
  border-radius: var(--radius);
}

.topbarRight {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  margin-left: auto;
}

.iconButton {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  font-size: var(--text-md);
  color: var(--text-muted);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.iconButton:hover {
  background: var(--bg-hover);
  color: var(--text);
}

.userMenu {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

/* The avatar and name, as one target leading to /profile. */
.userLink {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-1) var(--space-2);
  margin: calc(var(--space-1) * -1) calc(var(--space-2) * -1);
  color: inherit;
  text-decoration: none;
  border-radius: var(--radius);
  transition: background var(--transition-fast);
}

.userLink:hover {
  background: var(--bg-hover);
}

@media (prefers-reduced-motion: reduce) {
  .userLink {
    transition: none;
  }
}

.avatar {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  background: var(--indigo-soft);
  color: var(--indigo);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  border-radius: 50%;
  flex-shrink: 0;
}

.userInfo {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.userName {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--text);
}

.userEmail {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.signOut {
  padding: var(--space-1) var(--space-3);
  font-size: var(--text-sm);
  color: var(--text-muted);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.signOut:hover {
  background: var(--bg-hover);
  color: var(--text);
}

.content {
  flex: 1;
  padding: var(--space-6) var(--space-5);
  max-width: var(--content-max);
  width: 100%;
}

.backdrop {
  display: none;
}

/* ------------------------------------------------------------ responsive */
@media (max-width: 1024px) {
  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    z-index: var(--z-modal);
    transform: translateX(-100%);
    transition: transform var(--transition);
    box-shadow: var(--shadow-lg);
  }

  .sidebarOpen { transform: translateX(0); }

  .menuButton { display: block; }

  .backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: calc(var(--z-modal) - 1);
    background: rgb(15 23 42 / 45%);
    border: none;
  }

  .content { padding: var(--space-5) var(--space-4); }
}

@media (max-width: 640px) {
  .userInfo { display: none; }
  .content { padding: var(--space-4) var(--space-3); }
}
